export type AssetType =
  | 'folder'
  | 'sprite'
  | 'video'
  | 'audio-music'
  | 'audio-sfx'
  | 'script-js'
  | 'script-ts'
  | 'prefab'
  | 'scene'
  | 'font'
  | 'spine'
  | 'unknown';

export interface AssetMetadata {
  width?: number;
  height?: number;
  duration?: number;
  frameCount?: number;
  fileSize: number;
  mimeType?: string;
}

export interface Asset {
  id: string;
  name: string;
  path: string;
  type: AssetType;
  extension: string;
  parentId: string | null;
  metadata?: AssetMetadata;
  thumbnail?: string;
  createdAt: Date;
  modifiedAt: Date;
}

export interface FolderNode {
  id: string;
  name: string;
  path: string;
  parentId: string | null;
  children: string[];
  expanded: boolean;
  type: 'folder';
}

export type FileSystemNode = Asset | FolderNode;

export interface AssetFilter {
  search: string;
  types: AssetType[];
  sortBy: 'name' | 'type' | 'size' | 'modified';
  sortDirection: 'asc' | 'desc';
}

export const ASSET_ICONS: Record<AssetType, string> = {
  folder: '📁',
  sprite: '🖼️',
  video: '🎥',
  'audio-music': '🎵',
  'audio-sfx': '🔊',
  'script-js': '📜',
  'script-ts': '📜',
  prefab: '📦',
  scene: '🎬',
  font: '🔤',
  spine: '🦴',
  unknown: '📄',
};

export function getAssetIcon(type: AssetType): string {
  return ASSET_ICONS[type] || '📄';
}

/** Extension-specific icons for spine files (.skel = skeleton, .atlas = map) */
const SPINE_EXT_ICONS: Record<string, string> = {
  '.skel': '💀',
  '.atlas': '🗺️',
};

/** Returns a more specific icon when extension matters (e.g. spine sub-types) */
export function getAssetIconByExtension(asset: Asset): string {
  if (asset.type === 'spine') {
    return SPINE_EXT_ICONS[asset.extension.toLowerCase()] || ASSET_ICONS.spine;
  }
  return getAssetIcon(asset.type);
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

export function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}
